import jsPDF from 'jspdf';
import autoTable, { CellHookData } from 'jspdf-autotable';
import { TylerState } from '../utils/TylerState';

// ---------------------------------------------------------------------------
// Data shapes: what the optimizer returns (representative_scenario.year_by_year rows are nominal dollars)
// and what convertToBackendFormat posts (snake_case profile).
// ---------------------------------------------------------------------------

export interface Balances {
  rrsp?: number;
  tfsa?: number;
  non_registered?: number;
}

export interface YearRow {
  age: number;
  death?: boolean;
  inflation_factor?: number; // nominal / this = today's dollars
  employment_income?: number;
  cpp_income?: number;
  oas_final?: number;
  oas_clawed_back?: number;
  spouse_cpp_income?: number;
  spouse_oas_income?: number;
  pension_income?: number;
  other_income?: number;
  other_income_tax_free?: number;
  asset_sale_proceeds?: number;
  rrsp_withdrawal?: number;
  tfsa_withdrawal?: number;
  tfsa_contribution?: number;
  emergency_withdrawal_tfsa?: number;
  emergency_withdrawal_rrsp?: number;
  rrsp_investment_income?: number;
  tfsa_investment_income?: number;
  investment_income?: number; // non-registered growth
  annual_spending?: number;
  taxable_income?: number;
  taxes_paid?: number;
  ran_out_of_money?: boolean;
  balances?: Balances;
}

export interface Percentile {
  p10?: number;
  p25?: number;
  p50?: number;
  p75?: number;
  p90?: number;
}

export interface OptimizerResults {
  representative_scenario?: { year_by_year: YearRow[] };
  percentiles?: { estate_value?: Percentile; lifetime_taxes?: Percentile; death_age?: Percentile };
  distribution_stats?: { ran_out_probability?: number; avg_ran_out_age?: number | null };
  suggested_cpp_age?: number;
  suggested_oas_age?: number;
}

export interface OtherIncomeCard {
  description: string | null;
  amount: number; // today's dollars
  start_age: number;
  end_age: number | null;
  profile: string; // 'one time' | 'ongoing'
  income_type: string; // 'regular' | 'capital-gains' | 'non-taxable'
  cost_base: number;
}

export interface PostedProfile {
  age: number;
  retirement_age?: number;
  current_year?: number;
  spouse_age?: number | null;
  province?: string;
  rrsp_balance: number;
  tfsa_balance: number;
  non_registered_balance?: number;
  corporate_balance?: number;
  annual_spending: number;
  inflation_rate?: number;
  rrsp_return_rate?: number;
  tfsa_return_rate?: number;
  non_registered_return_rate?: number;
  other_income_sources?: OtherIncomeCard[] | null;
  spouse_other_income_sources?: OtherIncomeCard[] | null;
}

export type InflationView = 'real' | 'nominal';
type Cell = string | number;
type Rgb = [number, number, number];

// ---------------------------------------------------------------------------
// Pure calculations (unit-tested in retirement-pdf-service.test.ts)
// ---------------------------------------------------------------------------

/** Engine rows are nominal; the "real" view divides by that row's cumulative inflation factor. */
export const toView = (value: number | undefined, row: YearRow, view: InflationView): number =>
  (value || 0) / (view === 'real' ? (row.inflation_factor || 1) : 1);

export const totalBalance = (row: YearRow): number =>
  (row.balances?.rrsp || 0) + (row.balances?.tfsa || 0) + (row.balances?.non_registered || 0);

const registeredDraws = (row: YearRow) => ({
  rrsp: (row.rrsp_withdrawal || 0) + (row.emergency_withdrawal_rrsp || 0),
  tfsa: (row.tfsa_withdrawal || 0) + (row.emergency_withdrawal_tfsa || 0)
});

export interface CashFlowLine {
  label: string;
  amount: number;
  kind: 'income' | 'expense' | 'subtotal';
}

/** Where the year's money came from and where it went. Subtotals reconcile to the non-registered change before growth. */
const ALWAYS_SHOWN = ['CPP', 'OAS (after clawback)', 'RRSP/RRIF withdrawals', 'TFSA withdrawals'];

export const cashFlowLines = (row: YearRow, hasSpouse: boolean): CashFlowLine[] => {
  const draws = registeredDraws(row);
  const incomeLine = (label: string, amount: number): CashFlowLine => ({ label, amount, kind: 'income' });
  const candidates: CashFlowLine[] = [
    incomeLine('Employment income', row.employment_income || 0),
    incomeLine('CPP', row.cpp_income || 0),
    incomeLine('OAS (after clawback)', row.oas_final || 0)
  ];
  if (hasSpouse) candidates.push(incomeLine('Spouse CPP and OAS', (row.spouse_cpp_income || 0) + (row.spouse_oas_income || 0)));
  candidates.push(
    incomeLine('Pension', row.pension_income || 0),
    incomeLine('Other taxable income', row.other_income || 0),
    incomeLine('Tax-free amounts and sale proceeds', (row.other_income_tax_free || 0) + (row.asset_sale_proceeds || 0)),
    incomeLine('RRSP/RRIF withdrawals', draws.rrsp),
    incomeLine('TFSA withdrawals', draws.tfsa)
  );
  const income = candidates.filter(line => line.amount !== 0 || ALWAYS_SHOWN.includes(line.label));

  const totalIn = income.reduce((sum, line) => sum + line.amount, 0);
  const taxes = row.taxes_paid || 0;
  const tfsaTopUp = row.tfsa_contribution || 0;
  const available = totalIn - taxes - tfsaTopUp;
  const spending = row.annual_spending || 0;

  const lines: CashFlowLine[] = [
    ...income,
    { label: 'Total cash in', amount: totalIn, kind: 'subtotal' },
    { label: 'Income tax', amount: -taxes, kind: 'expense' }
  ];
  if (tfsaTopUp) lines.push({ label: 'Moved into TFSA', amount: -tfsaTopUp, kind: 'expense' });
  lines.push(
    { label: 'Available to spend', amount: available, kind: 'subtotal' },
    { label: 'Spending', amount: -spending, kind: 'expense' },
    { label: available - spending >= 0 ? 'Added to non-registered savings' : 'Drawn from non-registered savings', amount: available - spending, kind: 'subtotal' }
  );
  return lines;
};

export interface AccountYear {
  age: number;
  opening: number;
  growth: number;
  contributions: number;
  withdrawals: number;
  closing: number;
}

/** Opening -> growth -> contributions -> withdrawals -> closing for one account, in nominal dollars. */
export const accountContinuity = (
  rows: YearRow[],
  account: 'rrsp' | 'tfsa',
  startingBalance: number
): AccountYear[] => {
  let opening = startingBalance;
  return rows.filter(row => !row.death).map(row => {
    const draws = registeredDraws(row);
    const year: AccountYear = account === 'rrsp'
      ? { age: row.age, opening, growth: row.rrsp_investment_income || 0, contributions: 0, withdrawals: draws.rrsp, closing: row.balances?.rrsp || 0 }
      : { age: row.age, opening, growth: row.tfsa_investment_income || 0, contributions: row.tfsa_contribution || 0, withdrawals: draws.tfsa, closing: row.balances?.tfsa || 0 };
    opening = year.closing;
    return year;
  });
};

export interface NonRegisteredYear {
  age: number;
  opening: number;
  moneyIn: number;
  growth: number;
  spending: number;
  taxes: number;
  toTfsa: number;
  closing: number;
}

/** The non-registered account is the household chequing account: everything lands here and spending and tax leave from here. */
export const nonRegisteredContinuity = (rows: YearRow[], startingBalance: number): NonRegisteredYear[] => {
  let opening = startingBalance;
  return rows.filter(row => !row.death).map(row => {
    const draws = registeredDraws(row);
    const moneyIn = (row.employment_income || 0) + (row.cpp_income || 0) + (row.oas_final || 0) +
      (row.spouse_cpp_income || 0) + (row.spouse_oas_income || 0) + (row.pension_income || 0) +
      (row.other_income || 0) + (row.other_income_tax_free || 0) + (row.asset_sale_proceeds || 0) +
      draws.rrsp + draws.tfsa;
    const year: NonRegisteredYear = {
      age: row.age,
      opening,
      moneyIn,
      growth: row.investment_income || 0,
      spending: row.annual_spending || 0,
      taxes: row.taxes_paid || 0,
      toTfsa: row.tfsa_contribution || 0,
      closing: row.balances?.non_registered || 0
    };
    opening = year.closing;
    return year;
  });
};

// Federal brackets used for the plain-language tax page (2024)
const FEDERAL_BRACKETS = [
  { rate: 15, min: 0, max: 55867 },
  { rate: 20.5, min: 55867, max: 111733 },
  { rate: 26, min: 111733, max: 173205 },
  { rate: 29, min: 173205, max: 246752 },
  { rate: 33, min: 246752, max: Infinity }
];
// The engine taxes every province with Ontario rates until the province select is wired through
const ONTARIO_BRACKETS = [
  { rate: 5.05, min: 0, max: 51446 },
  { rate: 9.15, min: 51446, max: 102894 },
  { rate: 11.16, min: 102894, max: 150000 },
  { rate: 12.16, min: 150000, max: 220000 },
  { rate: 13.16, min: 220000, max: Infinity }
];

export const bracketFor = (income: number, brackets: { rate: number; min: number; max: number }[]) =>
  brackets.find(bracket => income >= bracket.min && income < bracket.max) || brackets[brackets.length - 1];

// ---------------------------------------------------------------------------
// Layout constants (A4 portrait, mm)
// ---------------------------------------------------------------------------

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 18;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;
const HEADER_BOTTOM = 30;
const FOOTER_TOP = PAGE_HEIGHT - 14;
const TOTAL_PAGES_TOKEN = '{total_pages_count_string}';

const BRAND_RED: Rgb = [220, 38, 38];
const INCOME_GREEN: Rgb = [5, 150, 105];
const EXPENSE_RED: Rgb = [220, 38, 38];
const INK: Rgb = [17, 24, 39];
const MUTED: Rgb = [107, 114, 128];
const RULE: Rgb = [229, 231, 235];
const HEAD_FILL: Rgb = [241, 245, 249];
const ZEBRA: Rgb = [248, 250, 252];
const TILE_FILL: Rgb = [254, 242, 242];

interface TableOptions {
  head: string[];
  body: Cell[][];
  startY: number;
  widths?: number[];
  incomeColumns?: number[];
  expenseColumns?: number[];
  boldLastRow?: boolean;
  fontSize?: number;
  keyValue?: boolean; // two text columns, both left-aligned, no header band
}

export class RetirementPDFService {
  private stampedPages = new Set<number>();
  private reportTitle = '';
  private reportDate = '';

  private money(amount: number): string {
    const rounded = Math.round(amount);
    const text = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(Math.abs(rounded));
    return rounded < 0 ? `(${text})` : text;
  }

  private percent(value: number): string {
    return `${value.toFixed(value % 1 === 0 ? 0 : 2)}%`;
  }

  // ----- page furniture -----

  /** Header band and footer, drawn once per page (autoTable also calls this when it adds pages). */
  private stampPage(pdf: jsPDF): void {
    const pageNumber = pdf.getCurrentPageInfo().pageNumber;
    if (this.stampedPages.has(pageNumber)) return;
    this.stampedPages.add(pageNumber);

    pdf.setFillColor(...BRAND_RED);
    pdf.rect(0, 0, PAGE_WIDTH, 16, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text("Larry's Help", MARGIN, 10.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(this.reportTitle, PAGE_WIDTH - MARGIN, 10.5, { align: 'right' });

    pdf.setDrawColor(...RULE);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, FOOTER_TOP, PAGE_WIDTH - MARGIN, FOOTER_TOP);
    pdf.setTextColor(...MUTED);
    pdf.setFontSize(8);
    pdf.text(`Prepared with LarrysHelp.org on ${this.reportDate}`, MARGIN, FOOTER_TOP + 5);
    pdf.text(`Page ${pageNumber} of ${TOTAL_PAGES_TOKEN}`, PAGE_WIDTH - MARGIN, FOOTER_TOP + 5, { align: 'right' });
    pdf.setTextColor(...INK);
  }

  private newPage(pdf: jsPDF): number {
    pdf.addPage();
    this.stampPage(pdf);
    return HEADER_BOTTOM;
  }

  /** Start a new page when fewer than `needed` mm remain. */
  private ensureRoom(pdf: jsPDF, y: number, needed: number): number {
    return y + needed > FOOTER_TOP - 4 ? this.newPage(pdf) : y;
  }

  private sectionTitle(pdf: jsPDF, text: string, y: number): number {
    y = this.ensureRoom(pdf, y, 30);
    pdf.setTextColor(...INK);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text(text, MARGIN, y);
    pdf.setDrawColor(...BRAND_RED);
    pdf.setLineWidth(0.8);
    pdf.line(MARGIN, y + 2, MARGIN + 14, y + 2);
    pdf.setFont('helvetica', 'normal');
    return y + 9;
  }

  private paragraph(pdf: jsPDF, text: string, y: number, options: { size?: number; muted?: boolean; indent?: number } = {}): number {
    const size = options.size ?? 9.5;
    const indent = options.indent ?? 0;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(size);
    pdf.setTextColor(...(options.muted ? MUTED : INK));
    const lines: string[] = pdf.splitTextToSize(text, CONTENT_WIDTH - indent);
    const lineHeight = size * 0.5;
    y = this.ensureRoom(pdf, y, lines.length * lineHeight + 2);
    lines.forEach(line => {
      pdf.text(line, MARGIN + indent, y);
      y += lineHeight;
    });
    pdf.setTextColor(...INK);
    return y + 2;
  }

  private bullets(pdf: jsPDF, items: string[], y: number): number {
    items.forEach(item => {
      y = this.ensureRoom(pdf, y, 10);
      pdf.setFillColor(...BRAND_RED);
      pdf.circle(MARGIN + 1.5, y - 1.2, 0.8, 'F');
      y = this.paragraph(pdf, item, y, { indent: 5 }) - 1;
    });
    return y + 2;
  }

  /** Big-number tile used on the summary page. */
  private tile(pdf: jsPDF, x: number, y: number, width: number, label: string, value: string, note: string): void {
    const height = 26;
    pdf.setFillColor(...TILE_FILL);
    pdf.roundedRect(x, y, width, height, 2, 2, 'F');
    pdf.setTextColor(...MUTED);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.text(label.toUpperCase(), x + 4, y + 6);
    pdf.setTextColor(...INK);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(value.length > 14 ? 12 : 16);
    pdf.text(value, x + 4, y + 15);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(...MUTED);
    const noteLines: string[] = pdf.splitTextToSize(note, width - 8);
    pdf.text(noteLines[0] || '', x + 4, y + 21);
    pdf.setTextColor(...INK);
  }

  /** Zebra table with a grey header, right-aligned numbers, income in green and expenses in red. Breaks pages by itself. */
  private table(pdf: jsPDF, options: TableOptions): number {
    const { head, body, widths, incomeColumns = [], expenseColumns = [], boldLastRow = false, keyValue = false } = options;
    const fontSize = options.fontSize ?? 8.5;
    const columnStyles: Record<number, { cellWidth?: number; halign?: 'left' | 'right'; fontStyle?: 'normal' | 'bold' }> = {};
    head.forEach((_, index) => {
      columnStyles[index] = {
        halign: index === 0 || keyValue ? 'left' : 'right',
        ...(keyValue && index === 0 ? { fontStyle: 'bold' } : {}),
        ...(widths ? { cellWidth: widths[index] } : {})
      };
    });

    autoTable(pdf, {
      startY: options.startY,
      head: [head],
      body,
      showHead: keyValue ? false : 'everyPage',
      theme: 'plain',
      margin: { left: MARGIN, right: MARGIN, top: HEADER_BOTTOM, bottom: PAGE_HEIGHT - FOOTER_TOP + 4 },
      styles: { font: 'helvetica', fontSize, cellPadding: { top: 1.6, bottom: 1.6, left: 2, right: 2 }, textColor: INK, lineWidth: 0 },
      headStyles: { fillColor: HEAD_FILL, textColor: INK, fontStyle: 'bold', fontSize: fontSize - 0.5 },
      alternateRowStyles: { fillColor: ZEBRA },
      columnStyles,
      didParseCell: (data: CellHookData) => {
        if (data.section !== 'body') return;
        if (incomeColumns.includes(data.column.index)) data.cell.styles.textColor = INCOME_GREEN;
        if (expenseColumns.includes(data.column.index)) data.cell.styles.textColor = EXPENSE_RED;
        if (boldLastRow && data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = HEAD_FILL;
        }
      },
      didDrawPage: () => this.stampPage(pdf)
    });

    return (pdf as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  }

  private keyValueTable(pdf: jsPDF, rows: [string, string][], y: number): number {
    return this.table(pdf, { head: ['', ''], body: rows, startY: y, widths: [58, CONTENT_WIDTH - 58], keyValue: true });
  }

  // ----- the report -----

  // eslint-disable-next-line max-lines-per-function, max-statements
  async generateProfessionalPDF(scenarioName: string = 'Retirement Plan'): Promise<void> {
    try {
      const results = TylerState.retirement.results as OptimizerResults | null;
      const profile = TylerState.retirement.userProfile as PostedProfile | null;
      const view: InflationView = TylerState.retirement.inflationView === 'nominal' ? 'nominal' : 'real';
      const locked = TylerState.retirement.benefitAgesLocked;

      if (!results || !profile) {
        alert('No retirement results to export. Please run analysis first.');
        return;
      }
      const rows = results.representative_scenario?.year_by_year || [];
      if (rows.length === 0) {
        alert('Results data incomplete. Please re-run analysis.');
        return;
      }

      this.stampedPages = new Set<number>();
      this.reportTitle = scenarioName;
      this.reportDate = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const v = (value: number | undefined, row: YearRow) => toView(value, row, view);
      const livingRows = rows.filter(row => !row.death);
      const firstYear = livingRows[0] || rows[0];
      const lastLiving = livingRows[livingRows.length - 1] || rows[rows.length - 1];
      const deathRow = rows.find(row => row.death);
      const hasSpouse = Boolean(profile.spouse_age);
      const baseYear = new Date().getFullYear(); // the profile's current_year default is stale (2025); the plan starts now
      const valuesShown = view === 'real' ? `Real dollars (${baseYear} value)` : 'Nominal dollars (future value, inflation included)';
      const ranOut = results.distribution_stats?.ran_out_probability ?? 0;
      const lastsPercent = Math.round((1 - ranOut) * 100);
      const avgRanOutAge = results.distribution_stats?.avg_ran_out_age;
      const ranOutSentence = ranOut > 0 && avgRanOutAge
        ? `In the ${100 - lastsPercent}% that fall short, the money runs out at age ${Math.round(avgRanOutAge)} on average. `
        : '';
      const estate = results.percentiles?.estate_value || {};
      const lifetimeTax = results.percentiles?.lifetime_taxes || {};
      const deathAge = results.percentiles?.death_age || {};
      const cppAge = results.suggested_cpp_age || 65;
      const oasAge = results.suggested_oas_age || 65;
      const timingNote = locked && !locked.cpp && !locked.oas ? 'chosen by the optimizer'
        : locked && locked.cpp && locked.oas ? 'as entered'
        : 'CPP ' + (locked?.cpp ? 'as entered' : 'optimized') + ', OAS ' + (locked?.oas ? 'as entered' : 'optimized');
      const otherIncomeCards = [
        ...(profile.other_income_sources || []).map(card => ({ ...card, owner: 'Me' })),
        ...(profile.spouse_other_income_sources || []).map(card => ({ ...card, owner: 'Spouse' }))
      ];

      // ============================ 1. SUMMARY ============================
      this.stampPage(pdf);
      let y = HEADER_BOTTOM;
      pdf.setTextColor(...INK);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.text('Your Retirement Plan', MARGIN, y);
      y += 8;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(...MUTED);
      pdf.text(`${scenarioName}  ·  ${this.reportDate}  ·  Values shown: ${valuesShown}`, MARGIN, y);
      pdf.setTextColor(...INK);
      y += 10;

      const tileWidth = (CONTENT_WIDTH - 3 * 4) / 4;
      const tiles: [string, string, string][] = [
        ['Plan reliability', `${lastsPercent}%`, 'of simulations last for life'],
        ['Ending balance', this.money(estate.p50 || 0), 'median estate at death'],
        ['Lifetime tax', this.money(lifetimeTax.p50 || 0), 'median income tax, all years'],
        ['Benefits start', `CPP ${cppAge} · OAS ${oasAge}`, timingNote]
      ];
      tiles.forEach(([label, value, note], index) => this.tile(pdf, MARGIN + index * (tileWidth + 4), y, tileWidth, label, value, note));
      y += 34;

      y = this.sectionTitle(pdf, 'Your plan at a glance', y);
      const returns = `RRSP ${this.percent(profile.rrsp_return_rate || 0)} · TFSA ${this.percent(profile.tfsa_return_rate || 0)} · Non-registered ${this.percent(profile.non_registered_return_rate || 0)}`;
      y = this.keyValueTable(pdf, [
        ['Household', hasSpouse ? `Couple (you ${profile.age}, spouse ${profile.spouse_age})` : `Single, age ${profile.age}`],
        ['Retirement age', String(profile.retirement_age || 65)],
        ['Planning horizon', `Median life expectancy ${deathAge.p50 || lastLiving.age}; this projection runs to age ${lastLiving.age}`],
        ['Spending target', `${this.money(v(firstYear.annual_spending, firstYear))} per year (${this.money(v(firstYear.annual_spending, firstYear) / 12)} per month), after tax`],
        ['Inflation assumption', `${this.percent(profile.inflation_rate || 0)} per year (the simulation varies it between 0.5% and 5%)`],
        ['Expected returns', returns],
        ['Tax brackets', `${profile.province || 'Ontario'} selected; Ontario rates applied until provincial tax is wired through`]
      ], y);

      y = this.sectionTitle(pdf, 'Starting balances', y);
      const startingTotal = profile.rrsp_balance + profile.tfsa_balance + (profile.non_registered_balance || 0) + (profile.corporate_balance || 0);
      y = this.table(pdf, {
        head: ['Account', 'Balance today'],
        body: [
          ['RRSP/RRIF', this.money(profile.rrsp_balance)],
          ['TFSA', this.money(profile.tfsa_balance)],
          ['Non-registered', this.money(profile.non_registered_balance || 0)],
          ['Corporate', this.money(profile.corporate_balance || 0)],
          ['Total', this.money(startingTotal)]
        ],
        startY: y,
        widths: [100, CONTENT_WIDTH - 100],
        boldLastRow: true
      });

      if (otherIncomeCards.length > 0) {
        y = this.sectionTitle(pdf, 'Other income and asset sales in this plan', y);
        const treatment = (card: OtherIncomeCard) => card.income_type === 'non-taxable' ? 'Tax-free'
          : card.income_type === 'capital-gains' ? `Capital gain, cost base ${this.money(card.cost_base || 0)}` : 'Fully taxable';
        const when = (card: OtherIncomeCard) => card.profile === 'one time' ? `Age ${card.start_age}` : `Age ${card.start_age}${card.end_age ? `-${card.end_age}` : '+'} yearly`;
        y = this.table(pdf, {
          head: ['Source', 'When', `Amount (${baseYear} $)`, 'Tax treatment'],
          body: otherIncomeCards.map(card => [`${card.description || 'Other income'} (${card.owner})`, when(card), this.money(card.amount), treatment(card)]),
          startY: y,
          widths: [64, 30, 34, CONTENT_WIDTH - 128]
        });
        y = this.paragraph(pdf, 'Amounts grow with inflation until the year received; proceeds are added to non-registered savings.', y, { size: 8, muted: true });
      }

      // ============================ 2. STRATEGY ============================
      y = this.newPage(pdf);
      y = this.sectionTitle(pdf, 'How the plan works', y);
      y = this.paragraph(pdf,
        `Each year the plan pays your spending from fixed income first (employment, CPP, OAS, pensions, other income), then draws from the RRSP/RRIF, ` +
        `then from the TFSA. Surplus cash is kept in the non-registered account, where income tax is paid from. ` +
        `The optimizer tested thousands of combinations of benefit timing and RRSP draw levels and kept the one with the best median estate; ` +
        `the numbers in this report follow the single simulated path closest to that median.`, y);

      const retirementAge = profile.retirement_age || 65;
      if (retirementAge < 65) {
        y = this.sectionTitle(pdf, `Phase 1 · Early retirement (ages ${retirementAge}-64)`, y);
        y = this.bullets(pdf, [
          'No CPP or OAS yet: spending is funded from savings.',
          'Low-income years are used to draw down the RRSP at low tax rates before RRIF minimums begin.',
          'The TFSA is preserved for tax-free growth and flexibility.'
        ], y);
      }
      y = this.sectionTitle(pdf, `Phase 2 · Government benefits (ages ${Math.max(retirementAge, 65)}-70)`, y);
      y = this.bullets(pdf, [
        `CPP starts at ${cppAge} and OAS at ${oasAge} (${timingNote}). Every year CPP is delayed past 65 adds 8.4%; every year OAS is delayed adds 7.2%.`,
        'RRSP/RRIF draws are sized around the benefits to keep taxable income steady.',
        'Income above the OAS clawback threshold reduces OAS by 15 cents per dollar; the plan watches that line.'
      ], y);
      y = this.sectionTitle(pdf, 'Phase 3 · RRIF minimums (ages 71+)', y);
      y = this.bullets(pdf, [
        'The RRSP becomes a RRIF and mandatory minimum withdrawals apply, rising each year.',
        'Shortfalls are topped up from the TFSA, then from non-registered savings.',
        'At death, the remaining RRIF is taxed as income in the final return; TFSA and non-registered balances pass with little or no tax.'
      ], y);
      y = this.sectionTitle(pdf, 'Principles behind the numbers', y);
      y = this.bullets(pdf, [
        'Tax minimization: withdraw from accounts in the most tax-efficient order.',
        'Bracket management: fill low brackets, avoid jumping into high ones.',
        'Benefit timing: delay CPP and OAS when the lifetime value is higher.',
        'TFSA preservation: keep tax-free growth compounding as long as possible.',
        'Longevity: the plan is stress-tested against thousands of market and lifespan outcomes.'
      ], y);

      // ============================ 3. TAXES ============================
      y = this.newPage(pdf);
      y = this.sectionTitle(pdf, `Your tax picture in the first retirement year (age ${firstYear.age})`, y);
      const taxableIncome = firstYear.taxable_income ?? ((firstYear.employment_income || 0) + (firstYear.cpp_income || 0) + (firstYear.oas_final || 0) + (firstYear.pension_income || 0) + (firstYear.other_income || 0) + (firstYear.rrsp_withdrawal || 0));
      const federal = bracketFor(taxableIncome, FEDERAL_BRACKETS);
      const provincial = bracketFor(taxableIncome, ONTARIO_BRACKETS);
      const room = (max: number) => (max === Infinity ? 'top bracket' : this.money(max - taxableIncome));
      y = this.keyValueTable(pdf, [
        ['Whose return', hasSpouse ? 'One return: all household income is taxed on your return (spouse splitting is not modelled separately yet)' : 'Your return'],
        ['Taxable income', `${this.money(taxableIncome)} (nominal, as taxed that year)`],
        ['Income tax paid', this.money(firstYear.taxes_paid || 0)],
        ['Federal bracket', `${federal.rate}% on income from ${this.money(federal.min)}${federal.max === Infinity ? ' and up' : ` to ${this.money(federal.max)}`}`],
        ['Room before next federal bracket', room(federal.max)],
        ['Provincial bracket (Ontario rates)', `${provincial.rate}% on income from ${this.money(provincial.min)}${provincial.max === Infinity ? ' and up' : ` to ${this.money(provincial.max)}`}`],
        ['Room before next provincial bracket', room(provincial.max)]
      ], y);
      y = this.paragraph(pdf,
        '"Room" is how much more income could be taken this year before a higher rate applies. Filling a low bracket with RRSP withdrawals now can mean less tax later, when RRIF minimums and benefits stack up.',
        y, { size: 8.5, muted: true });

      y = this.sectionTitle(pdf, 'What the numbers suggest', y);
      const federalRoom = federal.max === Infinity ? 0 : federal.max - taxableIncome;
      const suggestions: string[] = [];
      if (federalRoom > 50000) suggestions.push(`Significant federal room (${this.money(federalRoom)}): larger RRSP withdrawals could be taken at today's rate.`);
      else if (federalRoom > 20000) suggestions.push(`Moderate federal room (${this.money(federalRoom)}) before the next bracket.`);
      else suggestions.push(`Little federal room (${this.money(federalRoom)}): additional withdrawals would be taxed at a higher rate.`);
      if ((firstYear.oas_clawed_back || 0) > 0) suggestions.push(`OAS is being clawed back (${this.money(firstYear.oas_clawed_back || 0)} in this year): income above the threshold costs 15 cents of OAS per dollar.`);
      else if (taxableIncome > 86912) suggestions.push('Income is in the OAS clawback zone from age 65: keep an eye on taxable income once OAS starts.');
      if (profile.rrsp_balance > 500000 && retirementAge < 65) suggestions.push('Large RRSP balance with early retirement: the years before 65 are the cheapest time to draw it down.');
      if (hasSpouse) suggestions.push('Two returns with pension income splitting would spread this income and could protect OAS; that is not yet modelled here.');
      y = this.bullets(pdf, suggestions, y);

      // ============================ 4. CASH FLOW ============================
      y = this.newPage(pdf);
      y = this.sectionTitle(pdf, `Where the money comes from · first retirement year (age ${firstYear.age})`, y);
      const lines = cashFlowLines(firstYear, hasSpouse);
      const incomeRows = lines.map(line => line.kind).map((kind, index) => (kind === 'income' ? index : -1)).filter(index => index >= 0);
      const expenseRows = lines.map((line, index) => (line.kind === 'expense' ? index : -1)).filter(index => index >= 0);
      autoTable(pdf, {
        startY: y,
        head: [['', 'Per year', 'Per month']],
        body: lines.map(line => [line.label, this.money(v(line.amount, firstYear)), this.money(v(line.amount, firstYear) / 12)]),
        theme: 'plain',
        margin: { left: MARGIN, right: MARGIN, top: HEADER_BOTTOM, bottom: PAGE_HEIGHT - FOOTER_TOP + 4 },
        styles: { font: 'helvetica', fontSize: 9, cellPadding: { top: 1.8, bottom: 1.8, left: 2, right: 2 }, textColor: INK, lineWidth: 0 },
        headStyles: { fillColor: HEAD_FILL, textColor: INK, fontStyle: 'bold' },
        columnStyles: { 0: { cellWidth: 94 }, 1: { halign: 'right', cellWidth: 40 }, 2: { halign: 'right', cellWidth: 40 } },
        didParseCell: (data: CellHookData) => {
          if (data.section !== 'body') return;
          const line = lines[data.row.index];
          if (line.kind === 'subtotal') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = HEAD_FILL;
          } else if (data.column.index > 0) {
            data.cell.styles.textColor = incomeRows.includes(data.row.index) ? INCOME_GREEN : expenseRows.includes(data.row.index) ? EXPENSE_RED : INK;
          }
        },
        didDrawPage: () => this.stampPage(pdf)
      });
      y = (pdf as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
      y = this.paragraph(pdf,
        'Cash from every source lands in the non-registered account; tax and spending are paid from it. ' +
        'A positive last line means the year ended with money added to savings; brackets mean savings were drawn down.',
        y, { size: 8.5, muted: true });

      // ============================ 5. ACCOUNTS ============================
      const rrsp = accountContinuity(livingRows, 'rrsp', profile.rrsp_balance);
      const tfsa = accountContinuity(livingRows, 'tfsa', profile.tfsa_balance);
      const nonReg = nonRegisteredContinuity(livingRows, profile.non_registered_balance || 0);
      const rowFor = (age: number) => livingRows.find(row => row.age === age) || firstYear;

      y = this.newPage(pdf);
      y = this.sectionTitle(pdf, `RRSP/RRIF year by year (${valuesShown})`, y);
      y = this.table(pdf, {
        head: ['Age', 'Opening', 'Growth', 'Withdrawals', 'Closing'],
        body: rrsp.map(year => [String(year.age), this.money(v(year.opening, rowFor(year.age))), this.money(v(year.growth, rowFor(year.age))), this.money(v(year.withdrawals, rowFor(year.age))), this.money(v(year.closing, rowFor(year.age)))]),
        startY: y, fontSize: 8, incomeColumns: [2], expenseColumns: [3]
      });
      y = this.paragraph(pdf, 'Withdrawals include the RRIF minimum from age 71 and any top-up needed to meet spending. Growth is the simulated return for that year.', y, { size: 8, muted: true });

      y = this.newPage(pdf);
      y = this.sectionTitle(pdf, `TFSA year by year (${valuesShown})`, y);
      y = this.table(pdf, {
        head: ['Age', 'Opening', 'Growth', 'Contributions', 'Withdrawals', 'Closing'],
        body: tfsa.map(year => [String(year.age), this.money(v(year.opening, rowFor(year.age))), this.money(v(year.growth, rowFor(year.age))), this.money(v(year.contributions, rowFor(year.age))), this.money(v(year.withdrawals, rowFor(year.age))), this.money(v(year.closing, rowFor(year.age)))]),
        startY: y, fontSize: 8, incomeColumns: [2, 3], expenseColumns: [4]
      });
      y = this.paragraph(pdf, 'Surplus cash is moved into the TFSA while contribution room allows; withdrawals cover spending the RRSP/RRIF did not.', y, { size: 8, muted: true });

      y = this.newPage(pdf);
      y = this.sectionTitle(pdf, `Non-registered savings year by year (${valuesShown})`, y);
      y = this.table(pdf, {
        head: ['Age', 'Opening', 'Money in', 'Growth', 'Spending', 'Taxes', 'To TFSA', 'Closing'],
        body: nonReg.map(year => {
          const row = rowFor(year.age);
          return [String(year.age), this.money(v(year.opening, row)), this.money(v(year.moneyIn, row)), this.money(v(year.growth, row)), this.money(v(year.spending, row)), this.money(v(year.taxes, row)), this.money(v(year.toTfsa, row)), this.money(v(year.closing, row))];
        }),
        startY: y, fontSize: 7.5, incomeColumns: [2, 3], expenseColumns: [4, 5, 6]
      });
      y = this.paragraph(pdf, '"Money in" is every dollar received that year: benefits, pensions, other income, sale proceeds and RRSP/RRIF and TFSA withdrawals.', y, { size: 8, muted: true });

      // ============================ 6. YEAR BY YEAR ============================
      y = this.newPage(pdf);
      y = this.sectionTitle(pdf, `The whole plan year by year (${valuesShown})`, y);
      const yearBody: Cell[][] = livingRows.map(row => {
        const draws = registeredDraws(row);
        const benefits = (row.cpp_income || 0) + (row.oas_final || 0) + (row.spouse_cpp_income || 0) + (row.spouse_oas_income || 0);
        const otherIncome = (row.employment_income || 0) + (row.pension_income || 0) + (row.other_income || 0) + (row.other_income_tax_free || 0) + (row.asset_sale_proceeds || 0);
        return [String(row.age), this.money(v(benefits, row)), this.money(v(otherIncome, row)), this.money(v(draws.rrsp, row)), this.money(v(draws.tfsa, row)), this.money(v(row.taxes_paid, row)), this.money(v(row.annual_spending, row)), this.money(v(totalBalance(row), row))];
      });
      if (deathRow) {
        yearBody.push([`Estate at ${deathRow.age}`, '', '', '', '', this.money(v(deathRow.taxes_paid, deathRow)), '', this.money(v(totalBalance(deathRow), deathRow))]);
      }
      y = this.table(pdf, {
        head: ['Age', 'CPP + OAS', 'Pension + other', 'RRSP draw', 'TFSA draw', 'Taxes', 'Spending', 'Total savings'],
        body: yearBody, startY: y, fontSize: 7.5, incomeColumns: [1, 2, 3, 4], expenseColumns: [5, 6], boldLastRow: Boolean(deathRow)
      });
      const ranOutRow = livingRows.find(row => row.ran_out_of_money);
      if (ranOutRow) y = this.paragraph(pdf, `Savings were exhausted at age ${ranOutRow.age} in this projection; spending after that is not funded.`, y, { size: 8.5 });
      if (deathRow) y = this.paragraph(pdf, 'The estate line shows the final tax bill (the RRIF is fully taxable at death) and what remains for heirs.', y, { size: 8, muted: true });

      // ============================ 7. RISK ============================
      y = this.newPage(pdf);
      y = this.sectionTitle(pdf, 'How reliable is this plan?', y);
      const health = ranOut >= 0.5 ? 'High risk' : ranOut >= 0.2 ? 'Moderate risk' : ranOut >= 0.05 ? 'Low risk' : 'Very safe';
      y = this.paragraph(pdf,
        `${health}: money lasts to the end of life in ${lastsPercent}% of the thousands of simulated market and lifespan outcomes. ` +
        ranOutSentence +
        'The tables in this report follow one representative path; the ranges below show how widely the outcomes spread.', y);
      const pct = (p: Percentile, key: keyof Percentile) => this.money(p[key] || 0);
      y = this.table(pdf, {
        head: ['', 'Poor markets (10th)', 'Below average (25th)', 'Median (50th)', 'Above average (75th)', 'Strong markets (90th)'],
        body: [
          ['Ending balance (estate)', pct(estate, 'p10'), pct(estate, 'p25'), pct(estate, 'p50'), pct(estate, 'p75'), pct(estate, 'p90')],
          ['Lifetime income tax', pct(lifetimeTax, 'p10'), pct(lifetimeTax, 'p25'), pct(lifetimeTax, 'p50'), pct(lifetimeTax, 'p75'), pct(lifetimeTax, 'p90')],
          ['Age at death', String(deathAge.p10 || ''), String(deathAge.p25 || ''), String(deathAge.p50 || ''), String(deathAge.p75 || ''), String(deathAge.p90 || '')]
        ],
        startY: y, fontSize: 8, widths: [44, 26, 26, 26, 26, 26]
      });

      y = this.sectionTitle(pdf, 'Important', y);
      y = this.bullets(pdf, [
        'This analysis is for education and planning only. It is not financial, tax or legal advice.',
        'Results depend on assumptions about markets, inflation, tax rules and lifespan; real outcomes will differ.',
        'Tax rules and government benefit rates change every year; the brackets used are the 2024 federal and Ontario rates.',
        'A home that is kept is not part of the estate figures; only financial accounts are counted.',
        'Talk to a qualified advisor before acting on any strategy in this report.'
      ], y);

      pdf.putTotalPages(TOTAL_PAGES_TOKEN);
      pdf.save(`retirement-plan-${scenarioName.toLowerCase().replace(/\s+/g, '-')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please check console for details.');
    }
  }
}

export default RetirementPDFService;
